import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Header height offset for scroll calculations
// Includes both header rows (main nav + secondary icons row)
const HEADER_OFFSET = 140;

/**
 * ScrollToTop component that handles:
 * 1. Scroll to top on pathname change (unless hash is present)
 * 2. Scroll to hash section with header offset
 * 3. Retry mechanism for async-rendered sections
 */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const lastPathRef = useRef(pathname);
  const lastHashRef = useRef(hash);
  const scrollAttemptRef = useRef(0);
  const scrollTimeoutRef = useRef(null);

  // Scroll to element by ID with header offset
  const scrollToElement = useCallback((elementId, maxAttempts = 25, initialDelay = 0) => {
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const attemptScroll = () => {
      const element = document.getElementById(elementId);
      
      if (element) {
        // Found the element - scroll to it with offset
        // Use getBoundingClientRect for accurate positioning
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + scrollTop - HEADER_OFFSET;
        
        // Force scroll to top first to avoid partial scroll issues
        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: 'smooth'
        });
        
        scrollAttemptRef.current = 0;
        return true;
      }
      
      // Element not found yet - retry if attempts remaining
      scrollAttemptRef.current++;
      if (scrollAttemptRef.current < maxAttempts) {
        // Increase delay between attempts for async content
        const delay = Math.min(200 + (scrollAttemptRef.current * 50), 500);
        scrollTimeoutRef.current = setTimeout(attemptScroll, delay);
      }
      return false;
    };

    // Start attempting to scroll after initial delay
    scrollAttemptRef.current = 0;
    if (initialDelay > 0) {
      scrollTimeoutRef.current = setTimeout(attemptScroll, initialDelay);
    } else {
      attemptScroll();
    }
  }, []);

  useEffect(() => {
    const pathChanged = lastPathRef.current !== pathname;
    const hashChanged = lastHashRef.current !== hash;
    lastPathRef.current = pathname;
    lastHashRef.current = hash;

    // If there's a hash, scroll to that element
    if (hash) {
      const elementId = hash.replace('#', '');
      
      if (pathChanged) {
        // Navigating from another page - need longer initial delay
        // to ensure all sections (including async ones) are mounted
        requestAnimationFrame(() => {
          // Wait for initial render, then start scroll attempts
          scrollToElement(elementId, 30, 300);
        });
      } else if (hashChanged) {
        // Same page, different hash - small delay for smooth UX
        scrollToElement(elementId, 15, 50);
      }
    } else if (pathChanged) {
      // No hash and path changed - scroll to top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
    }

    // Cleanup timeout on unmount
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [pathname, hash, scrollToElement]);

  return null;
};
